<template>
  <aside class="sidebar" :class="{ collapsed: isCollapsed, 'view-as-offset': isViewingAs }" :style="sidebarStyle">
    <!-- Logo Zone: own background so any logo sits naturally -->
    <div class="logo-zone" :style="{ background: 'var(--color-surface, #ffffff)' }">
      <div v-if="!isCollapsed" class="sidebar-logo">
        <img 
          v-if="brandingLogo && !logoError" 
          :src="brandingLogo" 
          :alt="appName"
          @error="handleLogoError"
        />
        <h2 v-else class="text-logo-dark">{{ appName }}</h2>
      </div>
      <div v-else class="sidebar-logo sidebar-logo--collapsed">
        <img 
          v-if="brandingLogo && !logoError" 
          :src="brandingLogo" 
          :alt="appName"
          @error="handleLogoError"
          class="collapsed-logo"
        />
        <span v-else class="collapsed-initials">{{ appName.charAt(0) }}</span>
      </div>

      <!-- Toggle handle: floated on the bottom border of the logo zone -->
      <button @click="toggleSidebar" class="toggle-handle" :title="isCollapsed ? 'Expand' : 'Collapse'">
        <i :class="isCollapsed ? 'fas fa-chevron-right' : 'fas fa-chevron-left'"></i>
      </button>
    </div>
    
    <nav class="sidebar-nav">

      <!-- ══════════════════════════════════════════════════════ -->
      <!-- PLATFORM ADMIN / ENGINEER — AccessIQ staff            -->
      <!-- ══════════════════════════════════════════════════════ -->

      <!-- Client Management -->
      <div v-if="isPlatformUser" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">CLIENT MANAGEMENT</h3>
        <router-link to="/client-management" class="nav-item">
          <i class="fas fa-users-cog"></i>
          <span v-if="!isCollapsed">AIQ Clients</span>
        </router-link>
      </div>

      <!-- Products -->
      <div v-if="isPlatformUser" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">PRODUCTS</h3>

        <!-- Keep Me Covered (navigate + expand) -->
        <div class="nav-item nav-item-split">
          <router-link to="/coverage-risk" class="nav-split-link" @click="kmcCoverageExpanded = true">
            <i class="fas fa-chart-area"></i>
            <span v-if="!isCollapsed">Keep Me Covered</span>
          </router-link>
          <button
            v-if="!isCollapsed"
            type="button"
            class="submenu-chevron-btn"
            aria-label="Toggle Keep Me Covered submenu"
            @click="kmcCoverageExpanded = !kmcCoverageExpanded"
          >
            <i class="fas submenu-chevron" :class="kmcCoverageExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>
        <div v-if="kmcCoverageExpanded && !isCollapsed" class="submenu">
          <router-link to="/coverage-risk" class="nav-item sub-item">
            <i class="fas fa-chart-area"></i>
            <span>Coverage Risk Dashboard</span>
          </router-link>
          <router-link to="/dis-enrollment-risk" class="nav-item sub-item">
            <i class="fas fa-shield-alt"></i>
            <span>My Action Queue</span>
          </router-link>
          <router-link to="/kmc-select-report" class="nav-item sub-item">
            <i class="fas fa-clipboard-list"></i>
            <span>Eligibility Dashboard</span>
          </router-link>
        </div>

        <!-- Document IQ (navigate + expand) -->
        <div class="nav-item nav-item-split">
          <router-link to="/aiq-ai" class="nav-split-link" @click="kmcExpanded = true">
            <i class="fas fa-file-alt"></i>
            <span v-if="!isCollapsed">Document IQ</span>
          </router-link>
          <button
            v-if="!isCollapsed"
            type="button"
            class="submenu-chevron-btn"
            aria-label="Toggle Document IQ submenu"
            @click="kmcExpanded = !kmcExpanded"
          >
            <i class="fas submenu-chevron" :class="kmcExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>
        <div v-if="kmcExpanded && !isCollapsed" class="submenu">
          <router-link to="/aiq-ai" class="nav-item sub-item">
            <i class="fas fa-th-large"></i>
            <span>My Doc IQ's</span>
          </router-link>
          <router-link to="/aiq/queue" class="nav-item sub-item">
            <i class="fas fa-layer-group"></i>
            <span>Document Queue</span>
          </router-link>
        </div>
        <!-- Policy IQ (expandable) -->
        <div class="nav-item nav-item-split">
          <router-link to="/iq-hub" class="nav-split-link" @click="policyIQExpanded = true">
            <i class="fas fa-compass"></i>
            <span v-if="!isCollapsed">Policy IQ</span>
          </router-link>
          <button
            v-if="!isCollapsed"
            type="button"
            class="submenu-chevron-btn"
            aria-label="Toggle Policy IQ submenu"
            @click="policyIQExpanded = !policyIQExpanded"
          >
            <i class="fas submenu-chevron" :class="policyIQExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>
        <div v-if="policyIQExpanded && !isCollapsed" class="submenu">
          <router-link
            to="/iq-hub"
            class="nav-item sub-item"
            active-class=""
            exact-active-class=""
            :class="{ 'router-link-exact-active': isIqHubHomeNavActive }"
          >
            <i class="fas fa-home"></i>
            <span>IQ Hub</span>
          </router-link>
          <router-link
            :to="{ path: '/iq-hub', query: { tab: 'landscape' } }"
            class="nav-item sub-item"
            active-class=""
            exact-active-class=""
            :class="{ 'router-link-exact-active': isIqHubLandscapeNavActive }"
          >
            <i class="fas fa-map"></i>
            <span>Landscape</span>
          </router-link>
          <router-link to="/state-iq" class="nav-item sub-item">
            <i class="fas fa-landmark"></i>
            <span>State IQ</span>
          </router-link>
          <router-link to="/converging-signals" class="nav-item sub-item">
            <i class="fas fa-globe-americas"></i>
            <span>Global IQ</span>
          </router-link>
          <div class="nav-item sub-item nav-item-split">
            <router-link to="/what-if-iq" class="nav-split-link" @click="whatIfNavExpanded = true">
              <i class="fas fa-question-circle"></i>
              <span>What-If IQ</span>
              <span class="tool-badge">NEW</span>
            </router-link>
            <button
              type="button"
              class="submenu-chevron-btn"
              aria-label="Toggle What-If IQ submenu"
              @click="whatIfNavExpanded = !whatIfNavExpanded"
            >
              <i class="fas submenu-chevron" :class="whatIfNavExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
          <div v-if="whatIfNavExpanded" class="submenu submenu-nested">
            <router-link to="/what-if-iq/sessions" class="nav-item sub-item sub-item-indent">
              <i class="fas fa-folder-open"></i>
              <span>Saved Sessions</span>
            </router-link>
          </div>
          <router-link to="/docket-iq" class="nav-item sub-item">
            <i class="fas fa-gavel"></i>
            <span>Docket IQ</span>
          </router-link>
          <router-link to="/pollie-packets" class="nav-item sub-item">
            <i class="fas fa-folder-open"></i>
            <span>Pollie Packets</span>
          </router-link>
          <router-link to="/briefings" class="nav-item sub-item">
            <i class="fas fa-newspaper"></i>
            <span>Briefing</span>
          </router-link>
          <div class="nav-item sub-item nav-item-split">
            <router-link
              :to="{ path: '/policy-engine', query: { tab: 'artifacts', standalone: 'true' } }"
              class="nav-split-link"
              @click="artifactsNavExpanded = true"
            >
              <i class="fas fa-archive"></i>
              <span>Intelligence Artifacts</span>
            </router-link>
            <button
              type="button"
              class="submenu-chevron-btn"
              aria-label="Toggle Intelligence Artifacts submenu"
              @click="artifactsNavExpanded = !artifactsNavExpanded"
            >
              <i class="fas submenu-chevron" :class="artifactsNavExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
          <div v-if="artifactsNavExpanded" class="submenu submenu-nested">
            <router-link
              :to="{ path: '/policy-engine', query: { tab: 'artifact-compare', standalone: 'true' } }"
              class="nav-item sub-item sub-item-indent nav-sub-compare"
              title="Artifact Compare"
            >
              <span class="nav-stand-mirror-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="12" cy="8.5" rx="5.5" ry="6.5" fill="none" stroke="currentColor" stroke-width="1.6" />
                  <path d="M9.5 6.5c1.2 1.8 1.2 3.7 0 5.5" fill="none" stroke="currentColor" stroke-width="1" opacity="0.45" stroke-linecap="round" />
                  <rect x="11.15" y="14.5" width="1.7" height="5.2" rx="0.4" fill="currentColor" />
                  <rect x="8" y="19.2" width="8" height="1.4" rx="0.7" fill="currentColor" />
                </svg>
              </span>
              <span class="nav-sub-compare-label">Artifact Compare</span>
            </router-link>
          </div>
          <router-link
            to="/iq-hub/provider-status"
            class="nav-item sub-item"
            :class="{ 'router-link-exact-active': isProviderStatusNavActive }"
          >
            <i class="fas fa-shield-alt"></i>
            <span>Provider Status</span>
          </router-link>
        </div>

        <!-- Community IQ -->
        <router-link to="/resource-navigator" class="nav-item">
          <i class="fas fa-map-marker-alt"></i>
          <span v-if="!isCollapsed">Community IQ</span>
        </router-link>
        <!-- Advocacy IQ -->
        <router-link to="/advocacy-iq" class="nav-item">
          <i class="fas fa-bullhorn"></i>
          <span v-if="!isCollapsed">Advocacy IQ</span>
        </router-link>
      </div>

      <!-- Policy IQ Admin -->
      <div v-if="isPlatformUser" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">ADMIN MENU</h3>
        <div class="nav-item nav-item-toggle" @click="policyIQAdminExpanded = !policyIQAdminExpanded">
          <i class="fas fa-user-shield"></i>
          <span v-if="!isCollapsed">Admin Menu</span>
          <i v-if="!isCollapsed" class="fas submenu-chevron" :class="policyIQAdminExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="policyIQAdminExpanded && !isCollapsed" class="submenu">
          <router-link to="/policy-engine" class="nav-item sub-item">
            <i class="fas fa-satellite-dish"></i>
            <span>Policy IQ Engine</span>
          </router-link>
          <router-link to="/market-intelligence" class="nav-item sub-item">
            <i class="fas fa-chart-line"></i>
            <span>Rate Intelligence</span>
          </router-link>
          <router-link v-if="isActualPlatformUser || isDemoOrg" to="/policy-delivery-matrix" class="nav-item sub-item">
            <i class="fas fa-paper-plane"></i>
            <span>Digest Manager</span>
            <span
              v-if="isActualPlatformUser && jurisdictionPendingCount > 0"
              class="nav-badge"
              :title="`${jurisdictionPendingCount} need jurisdiction assignment`"
            >{{ jurisdictionPendingCount > 99 ? '99+' : jurisdictionPendingCount }}</span>
          </router-link>
          <router-link to="/source-performance" class="nav-item sub-item">
            <i class="fas fa-chart-bar"></i>
            <span>Source Performance</span>
          </router-link>
          <!-- DEV ONLY — remove before launch -->
          <router-link to="/docket-iq?preview_setup=1" class="nav-item sub-item">
            <i class="fas fa-magic"></i>
            <span>Docket IQ Setup</span>
            <span class="tool-badge">DEV</span>
          </router-link>
          <router-link v-if="isActualPlatformUser" to="/internal/standup" class="nav-item sub-item">
            <i class="fas fa-clipboard-list"></i>
            <span>Standup Report</span>
          </router-link>
          <router-link v-if="isActualPlatformUser" to="/internal/demo-studio" class="nav-item sub-item">
            <i class="fas fa-play-circle"></i>
            <span>Demo Studio</span>
          </router-link>
          <router-link v-if="isActualPlatformUser" to="/internal/product-landing" class="nav-item sub-item">
            <i class="fas fa-th-large"></i>
            <span>Product Landing</span>
          </router-link>
        </div>
      </div>

      <!-- AccessIQ Tools — combined with expandable subcategories -->
      <div v-if="isPlatformUser" class="nav-section">
        <button
          v-if="!isCollapsed"
          type="button"
          class="nav-section-toggle"
          @click="accessIQToolsSectionExpanded = !accessIQToolsSectionExpanded"
        >
          <h3 class="nav-section-title">ACCESSIQ TOOLS</h3>
          <i
            class="fas submenu-chevron section-chevron"
            :class="accessIQToolsSectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
        <div v-if="accessIQToolsSectionExpanded || isCollapsed">

        <router-link to="/state-build" class="nav-item">
          <i class="fas fa-flag-usa"></i>
          <span v-if="!isCollapsed">States</span>
        </router-link>

        <!-- Connections -->
        <div class="nav-item nav-item-toggle" @click="aiqConnectionsExpanded = !aiqConnectionsExpanded">
          <i class="fas fa-plug"></i>
          <span v-if="!isCollapsed">Connections</span>
          <i v-if="!isCollapsed" class="fas submenu-chevron" :class="aiqConnectionsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="aiqConnectionsExpanded && !isCollapsed" class="submenu">
          <router-link to="/database-connections" class="nav-item sub-item">
            <i class="fas fa-database"></i>
            <span>Database Connections</span>
          </router-link>
          <router-link to="/api-integrations" class="nav-item sub-item">
            <i class="fas fa-plug"></i>
            <span>API Integrations</span>
          </router-link>
          <router-link to="/data-subscriptions" class="nav-item sub-item">
            <i class="fas fa-rss"></i>
            <span>Data Subscriptions</span>
          </router-link>
        </div>

        <!-- Engines -->
        <div class="nav-item nav-item-toggle" @click="aiqEnginesExpanded = !aiqEnginesExpanded">
          <i class="fas fa-cogs"></i>
          <span v-if="!isCollapsed">Engines</span>
          <i v-if="!isCollapsed" class="fas submenu-chevron" :class="aiqEnginesExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="aiqEnginesExpanded && !isCollapsed" class="submenu">
          <router-link to="/workflow-from-document" class="nav-item sub-item">
            <i class="fas fa-robot"></i>
            <span>AI Check Builder</span>
          </router-link>
          <router-link to="/prompt-enhancement" class="nav-item sub-item">
            <i class="fas fa-fire"></i>
            <span>Prompt Enhancement</span>
          </router-link>
          <router-link to="/knowledge-graph" class="nav-item sub-item">
            <i class="fas fa-project-diagram"></i>
            <span>Knowledge Graph</span>
          </router-link>
          <router-link to="/duplicate-detection" class="nav-item sub-item">
            <i class="fas fa-clone"></i>
            <span>Duplicate Detection</span>
          </router-link>
          <router-link to="/ecp/import" class="nav-item sub-item">
            <i class="fas fa-file-import"></i>
            <span>Data Imports</span>
          </router-link>
          <router-link to="/ecp/eligibility" class="nav-item sub-item">
            <i class="fas fa-shield-alt"></i>
            <span>Stedi Check</span>
          </router-link>
          <router-link to="/risk-indicator-library" class="nav-item sub-item">
            <i class="fas fa-sliders-h"></i>
            <span>Risk Indicators</span>
          </router-link>
        </div>

        <!-- Settings -->
        <div class="nav-item nav-item-toggle" @click="aiqSettingsExpanded = !aiqSettingsExpanded">
          <i class="fas fa-sliders-h"></i>
          <span v-if="!isCollapsed">Settings</span>
          <i v-if="!isCollapsed" class="fas submenu-chevron" :class="aiqSettingsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="aiqSettingsExpanded && !isCollapsed" class="submenu">
          <router-link to="/settings/branding" class="nav-item sub-item">
            <i class="fas fa-palette"></i>
            <span>Branding & Themes</span>
          </router-link>
          <router-link v-if="canAssignPlatformAdmin" to="/settings/users" class="nav-item sub-item">
            <i class="fas fa-user-cog"></i>
            <span>AccessIQ Staff</span>
          </router-link>
        </div>

        <!-- Financials -->
        <div class="nav-item nav-item-toggle" @click="aiqFinancialsExpanded = !aiqFinancialsExpanded">
          <i class="fas fa-dollar-sign"></i>
          <span v-if="!isCollapsed">Financials</span>
          <i v-if="!isCollapsed" class="fas submenu-chevron" :class="aiqFinancialsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </div>
        <div v-if="aiqFinancialsExpanded && !isCollapsed" class="submenu">
          <router-link to="/settings/billing" class="nav-item sub-item">
            <i class="fas fa-credit-card"></i>
            <span>Billing & Subscription</span>
          </router-link>
          <router-link to="/settings/payments" class="nav-item sub-item">
            <i class="fas fa-university"></i>
            <span>Payment Processing</span>
          </router-link>
        </div>
        </div>
      </div>

      <!-- Learning Management -->
      <div v-if="isPlatformUser" class="nav-section">
        <button
          v-if="!isCollapsed"
          type="button"
          class="nav-section-toggle"
          @click="learningSectionExpanded = !learningSectionExpanded"
        >
          <h3 class="nav-section-title">LEARNING MANAGEMENT</h3>
          <i
            class="fas submenu-chevron section-chevron"
            :class="learningSectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
        <div v-if="learningSectionExpanded || isCollapsed">
        <router-link to="/learning" class="nav-item">
          <i class="fas fa-book-open"></i>
          <span v-if="!isCollapsed">Course Catalog</span>
        </router-link>
        <router-link to="/learning/my-courses" class="nav-item">
          <i class="fas fa-graduation-cap"></i>
          <span v-if="!isCollapsed">My Learning</span>
        </router-link>
        <router-link to="/learning/paths" class="nav-item">
          <i class="fas fa-route"></i>
          <span v-if="!isCollapsed">Learning Paths</span>
        </router-link>
        <router-link to="/learning/admin" class="nav-item">
          <i class="fas fa-cog"></i>
          <span v-if="!isCollapsed">Manage Courses</span>
        </router-link>
        </div>
      </div>

      <!-- Legacy — preserved, not actively used -->
      <div v-if="isPlatformUser" class="nav-section">
        <button
          v-if="!isCollapsed"
          type="button"
          class="nav-section-toggle"
          @click="legacySectionExpanded = !legacySectionExpanded"
        >
          <h3 class="nav-section-title">LEGACY</h3>
          <i
            class="fas submenu-chevron section-chevron"
            :class="legacySectionExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
          ></i>
        </button>
        <div v-if="legacySectionExpanded || isCollapsed">
        <router-link to="/regulatory-intelligence" class="nav-item">
          <i class="fas fa-chart-bar"></i>
          <span v-if="!isCollapsed">Regulatory IQ</span>
        </router-link>
        <router-link to="/regulatory/saved-views" class="nav-item">
          <i class="fas fa-bookmark"></i>
          <span v-if="!isCollapsed">Saved Views</span>
        </router-link>
        <router-link to="/regulatory/alerts" class="nav-item">
          <i class="fas fa-bell"></i>
          <span v-if="!isCollapsed">Alerts</span>
          <span v-if="unreadAlertsCount > 0" class="nav-badge">{{ unreadAlertsCount }}</span>
        </router-link>
        <router-link to="/policy-monitor" class="nav-item">
          <i class="fas fa-satellite-dish"></i>
          <span v-if="!isCollapsed">Policy Monitor</span>
        </router-link>
        <router-link to="/converging-signals-report" class="nav-item">
          <i class="fas fa-layer-group"></i>
          <span v-if="!isCollapsed">Signal Archive</span>
        </router-link>
        <router-link to="/source-catalog" class="nav-item">
          <i class="fas fa-book-medical"></i>
          <span v-if="!isCollapsed">Source Catalog</span>
        </router-link>
        <router-link to="/regulatory/source-manager" class="nav-item">
          <i class="fas fa-database"></i>
          <span v-if="!isCollapsed">Source Manager</span>
        </router-link>
        <router-link to="/decision-center" class="nav-item">
          <i class="fas fa-brain"></i>
          <span v-if="!isCollapsed">Decision Center</span>
        </router-link>
        <router-link to="/dis-enrollment-risk" class="nav-item">
          <i class="fas fa-shield-alt"></i>
          <span v-if="!isCollapsed">My Action Queue</span>
        </router-link>
        <router-link to="/clients" class="nav-item">
          <i class="fas fa-users"></i>
          <span v-if="!isCollapsed">Clients</span>
        </router-link>
        <router-link to="/intake-queue" class="nav-item">
          <i class="fas fa-inbox"></i>
          <span v-if="!isCollapsed">Document Intake</span>
        </router-link>
        <router-link to="/settings" class="nav-item">
          <i class="fas fa-cogs"></i>
          <span v-if="!isCollapsed">Platform Settings</span>
        </router-link>
        <router-link to="/ab-testing" class="nav-item">
          <i class="fas fa-flask"></i>
          <span v-if="!isCollapsed">A/B Testing</span>
        </router-link>
        <router-link to="/coming-soon?product=document_upload" class="nav-item">
          <i class="fas fa-upload"></i>
          <span v-if="!isCollapsed">Coming Soon</span>
        </router-link>
        </div>
      </div>

      <!-- ══════════════════════════════════════════════════════ -->
      <!-- CLIENT USERS — filtered by product + role            -->
      <!-- ══════════════════════════════════════════════════════ -->

      <!-- Keep Me Covered (client) -->
      <div v-if="isClientUser && hasCoverageRisk && !isAdvocacyGrViewer" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">KEEP ME COVERED</h3>
        <router-link to="/coverage-risk" class="nav-item">
          <i class="fas fa-chart-area"></i>
          <span v-if="!isCollapsed">Coverage Risk Dashboard</span>
        </router-link>
        <router-link to="/dis-enrollment-risk" class="nav-item">
          <i class="fas fa-shield-alt"></i>
          <span v-if="!isCollapsed">My Action Queue</span>
        </router-link>
        <router-link to="/kmc-select-report" class="nav-item">
          <i class="fas fa-clipboard-list"></i>
          <span v-if="!isCollapsed">Eligibility Dashboard</span>
        </router-link>
      </div>

      <!-- Document IQ (client) -->
      <div v-if="isClientUser && (hasAdi || hasKeepMeCovered) && !isAdvocacyGrViewer" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">DOCUMENT IQ</h3>
        <router-link v-if="hasAdi || hasKeepMeCovered" to="/aiq-ai" class="nav-item">
          <i class="fas fa-th-large"></i>
          <span v-if="!isCollapsed">My Doc IQ's</span>
        </router-link>
        <router-link v-if="hasAdi || hasKeepMeCovered" to="/aiq/queue" class="nav-item">
          <i class="fas fa-layer-group"></i>
          <span v-if="!isCollapsed">Document Queue</span>
        </router-link>
      </div>

      <!-- Policy IQ (client) -->
      <div v-if="isClientUser && hasPolicyEngine && !isAdvocacyGrViewer" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">POLICY IQ</h3>
        <div class="nav-item nav-item-split">
          <router-link to="/iq-hub" class="nav-split-link" @click="policyIQClientExpanded = true">
            <i class="fas fa-compass"></i>
            <span v-if="!isCollapsed">Policy IQ Engine</span>
          </router-link>
          <button
            v-if="!isCollapsed"
            type="button"
            class="submenu-chevron-btn"
            aria-label="Toggle Policy IQ submenu"
            @click="policyIQClientExpanded = !policyIQClientExpanded"
          >
            <i class="fas submenu-chevron" :class="policyIQClientExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>
        <div v-if="policyIQClientExpanded && !isCollapsed" class="submenu">
          <router-link
            v-if="showPiq('piq_iq_hub')"
            to="/iq-hub"
            class="nav-item sub-item"
            active-class=""
            exact-active-class=""
            :class="{ 'router-link-exact-active': isIqHubHomeNavActive }"
          >
            <i class="fas fa-home"></i>
            <span>IQ Hub</span>
          </router-link>
          <router-link
            v-if="showPiq('piq_landscape')"
            :to="{ path: '/iq-hub', query: { tab: 'landscape' } }"
            class="nav-item sub-item"
            active-class=""
            exact-active-class=""
            :class="{ 'router-link-exact-active': isIqHubLandscapeNavActive }"
          >
            <i class="fas fa-map"></i>
            <span>Landscape</span>
          </router-link>
          <router-link v-if="showPiq('piq_state_iq')" to="/state-iq" class="nav-item sub-item">
            <i class="fas fa-landmark"></i>
            <span>State IQ</span>
          </router-link>
          <router-link v-if="showPiq('piq_global_iq')" to="/converging-signals" class="nav-item sub-item">
            <i class="fas fa-globe-americas"></i>
            <span>Global IQ</span>
          </router-link>
          <div v-if="showPiq('piq_what_if')" class="nav-item sub-item nav-item-split">
            <router-link to="/what-if-iq" class="nav-split-link" @click="whatIfNavExpanded = true">
              <i class="fas fa-question-circle"></i>
              <span>What-If IQ</span>
              <span class="tool-badge">NEW</span>
            </router-link>
            <button
              type="button"
              class="submenu-chevron-btn"
              aria-label="Toggle What-If IQ submenu"
              @click="whatIfNavExpanded = !whatIfNavExpanded"
            >
              <i class="fas submenu-chevron" :class="whatIfNavExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
          <div v-if="showPiq('piq_what_if') && whatIfNavExpanded" class="submenu submenu-nested">
            <router-link to="/what-if-iq/sessions" class="nav-item sub-item sub-item-indent">
              <i class="fas fa-folder-open"></i>
              <span>Saved Sessions</span>
            </router-link>
          </div>
          <router-link v-if="showPiq('piq_docket')" to="/docket-iq" class="nav-item sub-item">
            <i class="fas fa-gavel"></i>
            <span>Docket IQ</span>
            <span class="tool-badge">NEW</span>
          </router-link>
          <router-link v-if="showPiq('piq_pollie')" to="/pollie-packets" class="nav-item sub-item">
            <i class="fas fa-folder-open"></i>
            <span>Pollie Packets</span>
          </router-link>
          <router-link v-if="showPiq('piq_briefing') || showPiq('piq_newsletter')" to="/briefings" class="nav-item sub-item">
            <i class="fas fa-newspaper"></i>
            <span>Briefing</span>
          </router-link>
          <div v-if="showPiq('piq_artifacts')" class="nav-item sub-item nav-item-split">
            <router-link
              :to="{ path: '/policy-engine', query: { tab: 'artifacts', standalone: 'true' } }"
              class="nav-split-link"
              @click="artifactsNavExpanded = true"
            >
              <i class="fas fa-archive"></i>
              <span>Intelligence Artifacts</span>
            </router-link>
            <button
              type="button"
              class="submenu-chevron-btn"
              aria-label="Toggle Intelligence Artifacts submenu"
              @click="artifactsNavExpanded = !artifactsNavExpanded"
            >
              <i class="fas submenu-chevron" :class="artifactsNavExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </div>
          <div v-if="showPiq('piq_artifacts') && artifactsNavExpanded" class="submenu submenu-nested">
            <router-link
              :to="{ path: '/policy-engine', query: { tab: 'artifact-compare', standalone: 'true' } }"
              class="nav-item sub-item sub-item-indent nav-sub-compare"
              title="Artifact Compare"
            >
              <span class="nav-stand-mirror-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="12" cy="8.5" rx="5.5" ry="6.5" fill="none" stroke="currentColor" stroke-width="1.6" />
                  <path d="M9.5 6.5c1.2 1.8 1.2 3.7 0 5.5" fill="none" stroke="currentColor" stroke-width="1" opacity="0.45" stroke-linecap="round" />
                  <rect x="11.15" y="14.5" width="1.7" height="5.2" rx="0.4" fill="currentColor" />
                  <rect x="8" y="19.2" width="8" height="1.4" rx="0.7" fill="currentColor" />
                </svg>
              </span>
              <span class="nav-sub-compare-label">Artifact Compare</span>
            </router-link>
          </div>
          <router-link
            v-if="showPiq('piq_provider_status')"
            to="/iq-hub/provider-status"
            class="nav-item sub-item"
            :class="{ 'router-link-exact-active': isProviderStatusNavActive }"
          >
            <i class="fas fa-shield-alt"></i>
            <span>Provider Status</span>
          </router-link>
        </div>
      </div>

      <!-- Community IQ -->
      <div v-if="isClientUser && hasCommunityIQ && !isAdvocacyGrViewer" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">COMMUNITY IQ</h3>
        <router-link to="/resource-navigator" class="nav-item">
          <i class="fas fa-map-marker-alt"></i>
          <span v-if="!isCollapsed">Community IQ</span>
        </router-link>
      </div>

      <!-- Advocacy IQ -->
      <div v-if="showAdvocacyNav" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">ADVOCACY IQ</h3>
        <router-link to="/advocacy-iq" class="nav-item">
          <i class="fas fa-bullhorn"></i>
          <span v-if="!isCollapsed">Advocacy IQ</span>
        </router-link>
      </div>

      <div v-if="showMemberListNav" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">CONTACTS</h3>
        <router-link to="/settings/members" class="nav-item">
          <i class="fas fa-users"></i>
          <span v-if="!isCollapsed">Contacts</span>
        </router-link>
      </div>

      <div v-if="clientOrgSetupRoute" class="nav-section">
        <h3 class="nav-section-title" v-if="!isCollapsed">ORGANIZATION</h3>
        <router-link :to="clientOrgSetupRoute" class="nav-item">
          <i class="fas fa-sitemap"></i>
          <span v-if="!isCollapsed">Org Setup</span>
        </router-link>
      </div>

    </nav>
    
    <div class="sidebar-footer">
      <!-- I Wonder If... — shown when IWI product is enabled (on by default) -->
      <router-link v-if="hasIwi" :to="iwiHomeRoute" class="nav-item iwi-pin">
        <i class="fas fa-lightbulb"></i>
        <span v-if="!isCollapsed">I Wonder If...</span>
      </router-link>


      <!-- Organization & User Info -->
      <div class="footer-info" v-if="user && !isCollapsed">

        <!-- Org badge — clickable switcher for platform admins and multi-org users -->
        <div class="organization-info">
          <button
            v-if="!isConferenceMode && (isActualPlatformUser || isMultiOrgUser)"
            class="organization-badge org-switcher-btn"
            :class="{ 'viewing-as-active': isViewingAs }"
            @click="switcherOpen = !switcherOpen"
            title="Switch organization"
          >
            <i class="fas fa-building"></i>
            <span class="organization-name">{{ displayOrgName }}</span>
            <span v-if="displayStage" class="subscription-tier">{{ displayStage }}</span>
            <i class="fas fa-chevron-up switcher-chevron" :class="{ 'flipped': !switcherOpen }"></i>
          </button>
          <div v-else class="organization-badge">
            <i class="fas fa-building"></i>
            <span class="organization-name">{{ displayOrgName }}</span>
            <span v-if="displayStage" class="subscription-tier">{{ displayStage }}</span>
          </div>
        </div>

        <!-- Simple org switcher popover (multi-org users like Matt) -->
        <div v-if="switcherOpen && isMultiOrgUser" class="nav-switcher-popover">
          <div class="switcher-search-label">Your organizations:</div>
          <div class="switcher-org-list">
            <button
              class="switcher-result-item"
              :class="{ 'switcher-org-active': !isViewingAs }"
              @click="switchToOrg(null)"
            >
              <i class="fas fa-building"></i>
              <span>{{ user && (user.organization.display_name || user.organization.name) }}</span>
              <span v-if="!isViewingAs" class="switcher-current-pill">Current</span>
            </button>
            <button
              v-for="org in additionalOrgs"
              :key="org.id"
              class="switcher-result-item"
              :class="{ 'switcher-org-active': assumedOrg && assumedOrg.id === org.id }"
              @click="switchToOrg(org)"
            >
              <i class="fas fa-building"></i>
              <span>{{ org.display_name || org.name }}</span>
              <span v-if="assumedOrg && assumedOrg.id === org.id" class="switcher-current-pill">Current</span>
            </button>
          </div>
        </div>

        <!-- Org switcher popover (platform admins only) -->
        <div v-if="switcherOpen && isActualPlatformUser" class="nav-switcher-popover">
          <!-- Active assumption -->
          <div v-if="isViewingAs" class="switcher-active-section">
            <div class="switcher-active-row">
              <span class="switcher-active-label">Viewing as:</span>
              <strong class="switcher-active-name">{{ assumedOrg.display_name || assumedOrg.name }}</strong>
              <button class="switcher-exit-btn" @click="openExitModal('exit')">
                <i class="fas fa-lock"></i> Exit
              </button>
            </div>
            <div class="switcher-role-row">
              <label class="switcher-role-label">Role:</label>
              <select
                class="switcher-role-select"
                :value="assumedRole"
                @change="changeAssumedRole($event.target.value)"
                @click.stop
              >
                <option value="ORG_ADMIN">Org Admin</option>
                <option value="COMPLIANCE_ADMIN">Risk Analyst</option>
                <option value="VIEWER">Viewer</option>
              </select>
            </div>
          </div>

          <div class="switcher-search-label">{{ isViewingAs ? 'Switch to a different org:' : 'Search for a client org:' }}</div>
          <input
            v-model="switcherQuery"
            class="switcher-search-input"
            placeholder="Type org name…"
            @input="searchOrgs"
            @click.stop
          />

          <!-- Org results -->
          <div v-if="switcherResults.length && !selectedOrgForSwitch" class="switcher-results">
            <button
              v-for="org in switcherResults"
              :key="org.id"
              class="switcher-result-item"
              @click="selectOrgForSwitch(org)"
            >
              <i class="fas fa-building"></i>
              <span>{{ org.display_name || org.name }}</span>
              <span v-if="stageLabels[org.setup_status]" class="switcher-tier-pill">{{ stageLabels[org.setup_status] }}</span>
            </button>
          </div>
          <div v-else-if="switcherQuery.length > 1 && !switcherResults.length && !selectedOrgForSwitch" class="switcher-empty">No orgs found</div>

          <!-- Role picker -->
          <div v-if="selectedOrgForSwitch" class="switcher-role-picker">
            <div class="switcher-selected-org">
              <i class="fas fa-building"></i>
              <strong>{{ selectedOrgForSwitch.display_name || selectedOrgForSwitch.name }}</strong>
              <button class="switcher-clear-btn" @click="selectedOrgForSwitch = null; switcherQuery = ''; switcherResults = []">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <label class="switcher-role-label">View as role:</label>
            <select v-model="switcherRole" class="switcher-role-select">
              <option value="ORG_ADMIN">Org Admin</option>
              <option value="COMPLIANCE_ADMIN">Risk Analyst</option>
              <option value="VIEWER">Viewer</option>
            </select>
            <button class="switcher-activate-btn" @click="isViewingAs ? openExitModal('switch') : activateViewAs()">
              <i class="fas fa-eye"></i> {{ isViewingAs ? 'Switch org' : 'Activate View' }}
            </button>
          </div>
        </div>

        <!-- User Info — avatar opens Pollie profile for staff, Org Setup for client admins -->
        <div class="user-info">
          <router-link
            v-if="avatarLink"
            :to="avatarLink.to"
            class="user-avatar user-avatar--link"
            :title="avatarLink.label"
            :aria-label="avatarLink.label"
          >
            <i class="fas fa-user-circle"></i>
          </router-link>
          <div v-else class="user-avatar">
            <i class="fas fa-user-circle"></i>
          </div>
          <div class="user-details">
            <div class="user-name">{{ user.first_name }} {{ user.last_name }}</div>
            <div class="user-role">{{ displayRoleLabel }}</div>
            <div v-if="isVerifiedMember" class="member-badge">
              <i class="fas fa-check-circle"></i>
              SCAV Member
            </div>
          </div>
        </div>
      </div>
      
      <!-- Logout + more tools -->
      <div class="footer-bottom-row">
        <a @click="logout" class="nav-item logout-btn">
          <i class="fas fa-sign-out-alt"></i>
          <span v-if="!isCollapsed">Logout</span>
        </a>
        <InternalFooter />
      </div>
    </div>
  </aside>

  <!-- Org switch overlay — persists across the page reload until the brief is ready -->
  <Teleport to="body">
    <div v-if="orgSwitching" class="org-switch-overlay">
      <div class="org-switch-card">
        <i class="fas fa-circle-notch fa-spin org-switch-spinner"></i>
        <div class="org-switch-label">{{ orgSwitchMessage }}</div>
        <div class="org-switch-sub">Loading intelligence… this takes a moment.</div>
      </div>
    </div>
  </Teleport>

  <!-- Demo exit / switch password modal — teleported to body to escape sidebar stacking context -->
  <Teleport to="body">
    <div v-if="showDemoExitModal" class="sidebar-demo-exit-overlay" @click.self="closeDemoExitModal">
      <div class="sidebar-demo-exit-modal" role="dialog" aria-modal="true">
        <div class="sidebar-demo-exit-header">
          <i class="fas fa-lock"></i>
          <span>{{ pendingDemoAction === 'exit' ? 'Exit demo view' : 'Switch org' }}</span>
        </div>
        <p class="sidebar-demo-exit-body">
          Enter the demo password to
          {{ pendingDemoAction === 'exit' ? 'exit the demo view.' : 'switch to a different org.' }}
        </p>
        <input
          ref="demoPasswordInput"
          v-model="demoExitPassword"
          type="password"
          class="sidebar-demo-exit-input"
          placeholder="Demo password"
          @keydown.enter="confirmDemoAction"
          @keydown.esc="closeDemoExitModal"
        />
        <p v-if="demoExitError" class="sidebar-demo-exit-error">
          <i class="fas fa-exclamation-circle"></i> {{ demoExitError }}
        </p>
        <div class="sidebar-demo-exit-actions">
          <button class="sidebar-demo-exit-cancel" @click="closeDemoExitModal">Cancel</button>
          <button class="sidebar-demo-exit-confirm" :disabled="!demoExitPassword" @click="confirmDemoAction">
            Confirm
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import auth from '@/services/auth';
import api from '@/services/api';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import { assumedOrgState, setAssumedOrg, clearAssumedOrg, updateAssumedOrgFields, setActiveOrgFlags } from '@/services/assumedOrg';
import { isConferenceMode } from '@/services/conferenceMode';
import { CLIENT_STAGE_LABELS, hasAnyPolicyIq } from '@/constants';
import { shouldShowPolicyIqModule } from '@/utils/policyIqAccess';
import { clientOrgSetupOrgId } from '@/utils/orgSetupAccess';
import { normalizeOrgStateCode, syncPolicyContextStateFromOrg, clearPolicyContextStateForNational } from '@/services/hubState';
import InternalFooter from '@/components/InternalFooter.vue';

export default {
  name: 'NavSidebar',
  components: {
    InternalFooter,
  },
  data() {
    return {
      isCollapsed: false,
      user: null,
      logoError: false,
      unreadAlertsCount: 0,
      jurisdictionPendingCount: 0,
      // Expandable product submenus
      kmcCoverageExpanded: false,
      kmcExpanded: false,
      policyIQExpanded: false,
      policyIQClientExpanded: false,
      policyIQAdminExpanded: false,
      artifactsNavExpanded: false,
      whatIfNavExpanded: false,
      // Collapsible nav sections (platform)
      accessIQToolsSectionExpanded: false,
      learningSectionExpanded: false,
      legacySectionExpanded: false,
      // Expandable AccessIQ Tools submenus
      aiqConnectionsExpanded: false,
      aiqEnginesExpanded: false,
      aiqSettingsExpanded: false,
      aiqFinancialsExpanded: false,
      // Org switcher
      switcherOpen: false,
      switcherQuery: '',
      switcherResults: [],
      selectedOrgForSwitch: null,
      switcherRole: 'ORG_ADMIN',
      // Org switch overlay (persists across reload until brief is ready)
      orgSwitching: false,
      orgSwitchMessage: '',
      orgSwitchSafetyTimer: null,
      // Demo exit modal
      showDemoExitModal: false,
      demoExitPassword: '',
      demoExitError: '',
      pendingDemoAction: null,
      // Program enrollment — keyed by program_key, value is true when actively enrolled
      enrolledProgramKeys: {},
    };
  },
  computed: {
    isAuthenticated() {
      this.$route.path;
      return auth.isAuthenticated();
    },
    isIqHubHomeNavActive() {
      return this.$route.path === '/iq-hub' && this.$route.query.tab !== 'landscape';
    },
    isIqHubLandscapeNavActive() {
      return this.$route.path === '/iq-hub' && this.$route.query.tab === 'landscape';
    },
    isProviderStatusNavActive() {
      return this.$route.path.startsWith('/iq-hub/provider-status');
    },
    userRole() {
      return auth.getUserRole();
    },
    userDisplayName() {
      return auth.getUserDisplayName();
    },
    userRoleDisplay() {
      const role = this.userRole;
      const roleMap = {
        'PLATFORM_ADMIN': 'Platform Admin',
        'PLATFORM_ENGINEER': 'Platform Engineer',
        'ORG_ADMIN': 'Org Admin',
        'HEALTHCARE_ADMIN': 'Healthcare Admin',
        'LMS_ADMIN': 'LMS Admin',
        'COMPLIANCE_ADMIN': 'Risk Analyst',
        'ANALYST': 'Risk Analyst',
        'LEARNER': 'Learner',
        'VIEWER': 'Viewer',
        'ADMIN': 'Admin',
        'RISK_MANAGER': 'Risk Manager',
      };
      return roleMap[role] || (role || '').replace(/_/g, ' ');
    },
    // ── Assumed-org display helpers ───────────────────────────────
    isActualPlatformUser() {
      return ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'].includes(auth.getUserRole());
    },
    additionalOrgs() {
      return auth.getAdditionalOrganizations();
    },
    isMultiOrgUser() {
      return this.additionalOrgs.length > 0 && !this.isActualPlatformUser;
    },
    assumedOrg() {
      return assumedOrgState.org;
    },
    displayOrgName() {
      if (assumedOrgState.org) return assumedOrgState.org.display_name || assumedOrgState.org.name;
      return auth.getOrganizationName();
    },
    stageLabels() {
      return CLIENT_STAGE_LABELS;
    },
    displayStage() {
      if (!this.isActualPlatformUser || !assumedOrgState.org) return '';
      return CLIENT_STAGE_LABELS[assumedOrgState.org.setup_status] || '';
    },
    displayRoleLabel() {
      if (assumedOrgState.role) {
        const roleMap = {
          'ORG_ADMIN': 'Org Admin',
          'COMPLIANCE_ADMIN': 'Risk Analyst',
          'VIEWER': 'Viewer',
        };
        return roleMap[assumedOrgState.role] || assumedOrgState.role;
      }
      return this.userRoleDisplay;
    },
    organizationName() {
      return auth.getOrganizationName();
    },
    subscriptionTier() {
      return auth.getSubscriptionTier();
    },
    isVerifiedMember() {
      return auth.isVerifiedMember();
    },
    brandingLogo() {
      return whitelabelBranding.getLogo();
    },
    appName() {
      return whitelabelBranding.getAppName();
    },
    sidebarStyle() {
      return {
        '--sidebar-primary': whitelabelBranding.getColor('primary'),
        '--sidebar-bg': whitelabelBranding.getColor('surface'),
      };
    },
    // ── View-as helpers ───────────────────────────────────────────
    isViewingAs() {
      return !!assumedOrgState.org;
    },
    assumedRole() {
      return assumedOrgState.role || 'ORG_ADMIN';
    },
    isDemoOrg() {
      return assumedOrgState.org?.slug === 'demo';
    },
    // ── Role helpers ──────────────────────────────────────────────
    canAssignPlatformAdmin() {
      return auth.getUser()?.can_assign_platform_admin === true;
    },
    isPlatformUser() {
      const user = auth.getUser();
      // Booth demo guests always get client nav — never platform admin surfaces
      if (user?.conference_guest_expires_at) return false;
      // Platform staff keep admin nav during conference prep/demo (Mary), not booth guests
      if (auth.hasPlatformAccess()) {
        if (this.isConferenceMode) return true;
        // View As client: client product nav (booth parity), but staff still use admin workspace from AIQueue Open
        if (assumedOrgState.org) return false;
        return true;
      }
      if (this.isConferenceMode) return false;
      if (assumedOrgState.org) return false;
      return false;
    },
    isClientUser() {
      return !this.isPlatformUser;
    },
    isOrgAdmin() {
      if (assumedOrgState.role) return assumedOrgState.role === 'ORG_ADMIN';
      return this.userRole === 'ORG_ADMIN';
    },
    canOpenPollieProfile() {
      // Org setup is AccessIQ staff only — clients never edit it
      return ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'].includes(this.userRole);
    },
    isViewer() {
      if (assumedOrgState.role) return assumedOrgState.role === 'VIEWER';
      return this.userRole === 'VIEWER';
    },
    // ── Product feature flags ─────────────────────────────────────
    orgFeatures() {
      if (assumedOrgState.org) {
        return assumedOrgState.org.features_enabled || {};
      }
      const org = auth.getUserOrganization();
      return (org && org.features_enabled) ? org.features_enabled : {};
    },
    hasCoverageRisk() {
      return !!this.orgFeatures.coverage;
    },
    hasKeepMeCovered() {
      return !!this.orgFeatures.aiq_intel;
    },
    hasPolicyEngine() {
      return hasAnyPolicyIq(this.orgFeatures);
    },
    hasAdvocacyIQ() {
      return !!this.orgFeatures.advocacy_iq;
    },
    hasCommunityIQ() {
      return !!this.orgFeatures.community_iq;
    },
    isAdvocacyGrViewer() {
      if (assumedOrgState.role) return assumedOrgState.role === 'ADVOCACY_GR';
      return this.userRole === 'ADVOCACY_GR';
    },
    showAdvocacyNav() {
      return (this.isClientUser && this.hasAdvocacyIQ) || this.isAdvocacyGrViewer;
    },
    clientOrgSetupRoute() {
      const orgId = clientOrgSetupOrgId();
      return orgId ? { path: '/onboard', query: { edit: String(orgId) } } : null;
    },
    avatarLink() {
      if (this.canOpenPollieProfile) return { to: '/settings/profile', label: 'Pollie profile' };
      if (this.clientOrgSetupRoute) return { to: this.clientOrgSetupRoute, label: 'Org Setup' };
      return null;
    },
    showMemberListNav() {
      const org = assumedOrgState.org || auth.getUserOrganization();
      return this.isClientUser && !this.isAdvocacyGrViewer
        && (org?.org_kind === 'HEALTHCARE_ASSOCIATION' || this.hasAdvocacyIQ);
    },
    hasIwi() {
      return this.orgFeatures.iwi === undefined ? true : !!this.orgFeatures.iwi;
    },
    iwiHomeRoute() {
      return this.isPlatformUser ? '/internal/feedback/queue' : '/internal/feedback';
    },
    hasAdi() {
      return !!this.orgFeatures.adi;
    },
    hasFamilyCoverageCheck() {
      return !!this.enrolledProgramKeys['family_coverage_check'];
    },
    isPolicyEngineOnly() {
      return this.hasPolicyEngine && !this.hasCoverageRisk && !this.hasAdi && !this.hasKeepMeCovered;
    },
    isConferenceMode() {
      return isConferenceMode();
    },
  },
  watch: {
    '$route'(to, from) {
      this.syncProductNavFromRoute(to);
      this.syncArtifactsNavFromRoute(to);
      if (
        this.isActualPlatformUser
        && (to.path === '/policy-delivery-matrix' || from?.path === '/policy-delivery-matrix')
      ) {
        this.loadJurisdictionPendingCount();
      }
    },
    isAuthenticated(isAuth) {
      if (isAuth) {
        this.user = auth.getUser();
        this.loadEnrolledPrograms();
      } else {
        this.user = null;
        this.enrolledProgramKeys = {};
      }
    },
    assumedOrg() {
      this.loadEnrolledPrograms();
    },
  },
  created() {
    this.syncProductNavFromRoute(this.$route);
    this.syncArtifactsNavFromRoute(this.$route);
    if (this.isAuthenticated) {
      this.user = auth.getUser();
      this.loadUnreadAlertsCount();
      this.loadJurisdictionPendingCount();
      this.loadEnrolledPrograms();
    }
    this._onAuthStateChanged = () => {
      this.user = auth.getUser();
    };
    this._onJurisdictionQueueChanged = () => {
      this.loadJurisdictionPendingCount();
    };
    window.addEventListener('auth-state-changed', this._onAuthStateChanged);
    window.addEventListener('jurisdiction-queue-changed', this._onJurisdictionQueueChanged);
    // Restore overlay after page reload if an org switch was in progress
    const savedMessage = sessionStorage.getItem('org_switch_message');
    if (savedMessage) {
      this.orgSwitchMessage = savedMessage;
      this.orgSwitching = true;
      this.orgSwitchSafetyTimer = setTimeout(() => this._clearOrgSwitchOverlay(), 120000);
      window.addEventListener('iqhub:brief-ready', this._onBriefReady);
    }
  },

  beforeUnmount() {
    if (this.orgSwitchSafetyTimer) clearTimeout(this.orgSwitchSafetyTimer);
    window.removeEventListener('iqhub:brief-ready', this._onBriefReady);
    window.removeEventListener('auth-state-changed', this._onAuthStateChanged);
    window.removeEventListener('jurisdiction-queue-changed', this._onJurisdictionQueueChanged);
  },
  methods: {
    showPiq(key) {
      return shouldShowPolicyIqModule(key);
    },
    syncProductNavFromRoute(route) {
      const path = route?.path || '';
      if (this.isKmcCoverageRoute(path)) {
        this.kmcCoverageExpanded = true;
      }
      if (this.isKmcAdiRoute(path)) {
        this.kmcExpanded = true;
      }
      // Provider Status lives under the Policy IQ submenu — expand it whenever
      // that route is active so the link is visible (not only when browsing IQ Hub).
      if (this.isPolicyIQRoute(path) || (path || '').startsWith('/iq-hub/provider-status')) {
        if (this.isPlatformUser) this.policyIQExpanded = true;
        if (this.isClientUser) this.policyIQClientExpanded = true;
      }
    },
    isKmcCoverageRoute(path) {
      const prefixes = ['/coverage-risk', '/dis-enrollment-risk', '/kmc-select-report'];
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    },
    isKmcAdiRoute(path) {
      const prefixes = [
        '/aiq-ai',
        '/aiq/queue',
        '/aiq/485',
        '/aiq/insurance-coverage',
        '/aiq/clinical-notes',
        '/plan-coverage-query',
        '/document-iq/start',
        '/ecp/import',
        '/ecp/485-extraction',
      ];
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    },
    isPolicyIQRoute(path) {
      const prefixes = [
        '/iq-hub',
        '/state-iq',
        '/converging-signals',
        '/what-if-iq',
        '/docket-iq',
        '/pollie-packets',
        '/briefings',
        '/policy-engine',
        '/policy-alignment',
      ];
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    },
    syncArtifactsNavFromRoute(route) {
      const path = route?.path || '';
      const tab = route?.query?.tab;
      if (tab === 'artifact-compare' || path === '/policy-alignment') {
        this.artifactsNavExpanded = true;
        if (this.isPlatformUser) this.policyIQExpanded = true;
        if (this.isClientUser) this.policyIQClientExpanded = true;
      }
      if (path === '/what-if-iq/sessions') {
        this.whatIfNavExpanded = true;
        if (this.isPlatformUser) this.policyIQExpanded = true;
        if (this.isClientUser) this.policyIQClientExpanded = true;
      }
      if (this.isAccessIQToolsRoute(path)) {
        this.accessIQToolsSectionExpanded = true;
      }
      if (path.startsWith('/learning')) {
        this.learningSectionExpanded = true;
      }
      if (this.isLegacyRoute(path)) {
        this.legacySectionExpanded = true;
      }
    },
    isAccessIQToolsRoute(path) {
      const prefixes = [
        '/state-build',
        '/database-connections',
        '/api-integrations',
        '/data-subscriptions',
        '/workflow-from-document',
        '/prompt-enhancement',
        '/knowledge-graph',
        '/duplicate-detection',
        '/ecp/',
        '/risk-indicator-library',
        '/settings/branding',
        '/settings/users',
        '/settings/billing',
        '/settings/payments',
      ];
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    },
    isLegacyRoute(path) {
      const prefixes = [
        '/regulatory-intelligence',
        '/regulatory/',
        '/policy-monitor',
        '/converging-signals-report',
        '/source-catalog',
        '/compliance-health',
        '/decision-center',
        '/clients',
        '/intake-queue',
        '/ab-testing',
        '/coming-soon',
      ];
      if (path === '/settings') return true;
      return prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    },
    async loadEnrolledPrograms() {
      try {
        const orgId = assumedOrgState.org?.id || auth.getUser()?.organization?.id;
        if (!orgId) return;
        const response = await api.get(`/users/organizations/${orgId}/programs/`);
        this.applyFreshOrgFlags(orgId, response.data);
        const programs = response.data.programs || [];
        const keys = {};
        for (const p of programs) {
          keys[p.key] = !!(p.enrollment && p.enrollment.is_active);
        }
        this.enrolledProgramKeys = keys;
      } catch {
        this.enrolledProgramKeys = {};
      }
    },
    applyFreshOrgFlags(orgId, data) {
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'features_enabled')) return;
      const fields = {
        features_enabled: data.features_enabled || {},
        organization_type: data.organization_type,
      };
      setActiveOrgFlags(orgId, fields.features_enabled);
      if (assumedOrgState.org && assumedOrgState.org.id === orgId) {
        updateAssumedOrgFields(fields);
      }
      const user = auth.getUser();
      if (user?.organization?.id === orgId) {
        user.organization.features_enabled = fields.features_enabled;
        if (fields.organization_type) user.organization.organization_type = fields.organization_type;
        auth.setUser(user);
      }
    },
    async loadUnreadAlertsCount() {
      try {
        const response = await api.get('/regulatory/alerts/unread_count/');
        this.unreadAlertsCount = response.data.unread_count || 0;
      } catch (error) {
        console.debug('Could not load alerts count:', error);
      }
    },
    async loadJurisdictionPendingCount() {
      if (!this.isActualPlatformUser) {
        this.jurisdictionPendingCount = 0;
        return;
      }
      try {
        const response = await api.get('/iq-hub/jurisdiction-review/pending/count/');
        this.jurisdictionPendingCount = response.data.pending_count || 0;
      } catch (error) {
        console.debug('Could not load jurisdiction pending count:', error);
      }
    },
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    handleLogoError() {
      this.logoError = true;
    },
    logout() {
      this.$router.push('/logout');
    },

    // ── Org switcher ─────────────────────────────────────────────
    _stateNameFromCode(code) {
      const map = {
        AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
        CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
        HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
        KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
        MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
        MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
        NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
        OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
        SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
        VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
        DC: 'Washington D.C.',
        AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands',
        PR: 'Puerto Rico', VI: 'U.S. Virgin Islands',
      };
      return map[code] || null;
    },

    _showOrgSwitchOverlay(stateCode, orgName) {
      const stateName = this._stateNameFromCode(stateCode);
      const label = stateName
        ? `Refreshing intel for ${stateName}`
        : `Refreshing intel for ${orgName}`;
      this.orgSwitchMessage = label;
      this.orgSwitching = true;
      sessionStorage.setItem('org_switch_message', label);
      // Safety timeout — hide overlay after 2 minutes no matter what
      if (this.orgSwitchSafetyTimer) clearTimeout(this.orgSwitchSafetyTimer);
      this.orgSwitchSafetyTimer = setTimeout(() => this._clearOrgSwitchOverlay(), 120000);
      // Listen for IQ Hub to signal brief is ready
      window.addEventListener('iqhub:brief-ready', this._onBriefReady);
    },

    _clearOrgSwitchOverlay() {
      this.orgSwitching = false;
      this.orgSwitchMessage = '';
      sessionStorage.removeItem('org_switch_message');
      if (this.orgSwitchSafetyTimer) { clearTimeout(this.orgSwitchSafetyTimer); this.orgSwitchSafetyTimer = null; }
      window.removeEventListener('iqhub:brief-ready', this._onBriefReady);
    },

    _onBriefReady() {
      this._clearOrgSwitchOverlay();
    },

    switchToOrg(org) {
      if (!org) {
        clearAssumedOrg();
        syncPolicyContextStateFromOrg(this.user?.organization);
      } else {
        setAssumedOrg(org, null);
        syncPolicyContextStateFromOrg(org);
      }
      this.switcherOpen = false;

      const stateCode = org
        ? normalizeOrgStateCode(org)
        : normalizeOrgStateCode(this.user?.organization);
      const orgName = org
        ? (org.display_name || org.name)
        : (this.user?.organization?.display_name || this.user?.organization?.name || 'your organization');

      // Always land on IQ Hub after an org switch — profile/settings are intentional clicks only
      if (this.$route.path !== '/iq-hub') {
        this.$router.push('/iq-hub');
      }

      this._showOrgSwitchOverlay(stateCode, orgName);
    },
    async searchOrgs() {
      if (this.switcherQuery.length < 2) { this.switcherResults = []; return; }
      try {
        const response = await api.get('/client-management/overview/', { params: { include_locations: 1 } });
        const orgs = response.data.organizations || [];
        const q = this.switcherQuery.toLowerCase();
        this.switcherResults = orgs
          .filter(o => `${o.display_name || ''} ${o.name || ''}`.toLowerCase().includes(q))
          .slice(0, 8);
      } catch { this.switcherResults = []; }
    },
    selectOrgForSwitch(org) {
      this.selectedOrgForSwitch = org;
      this.switcherResults = [];
      this.switcherRole = 'ORG_ADMIN';
    },
    activateViewAs() {
      if (!this.selectedOrgForSwitch) return;
      setAssumedOrg(this.selectedOrgForSwitch, this.switcherRole);
      syncPolicyContextStateFromOrg(this.selectedOrgForSwitch);
      this.switcherOpen = false;
      this.switcherQuery = '';
      this.switcherResults = [];
      this.selectedOrgForSwitch = null;
      this.loadEnrolledPrograms();
      if (this.$route.path !== '/iq-hub') {
        this.$router.push('/iq-hub');
      }
    },
    changeAssumedRole(newRole) {
      if (!assumedOrgState.org) return;
      setAssumedOrg(assumedOrgState.org, newRole);
    },
    exitViewAs() {
      clearAssumedOrg();
      clearPolicyContextStateForNational();
      this.switcherOpen = false;
      this.switcherQuery = '';
      this.switcherResults = [];
      this.selectedOrgForSwitch = null;
      this.loadEnrolledPrograms();
    },
    openExitModal(action) {
      this.pendingDemoAction = action;
      this.demoExitPassword = '';
      this.demoExitError = '';
      this.showDemoExitModal = true;
      this.$nextTick(() => {
        this.$refs.demoPasswordInput?.focus();
      });
    },
    closeDemoExitModal() {
      this.showDemoExitModal = false;
      this.demoExitPassword = '';
      this.demoExitError = '';
      this.pendingDemoAction = null;
    },
    confirmDemoAction() {
      if (!this.demoExitPassword) return;
      if (this.demoExitPassword !== 'DemoAccessIQ!') {
        this.demoExitError = 'Incorrect password. Please try again.';
        return;
      }
      if (this.pendingDemoAction === 'exit') {
        this.exitViewAs();
      } else if (this.pendingDemoAction === 'switch') {
        this.activateViewAs();
      }
      this.closeDemoExitModal();
    },
  }
};
</script>

<style scoped>
/* Main sidebar styling */
.sidebar {
  width: 250px;
  background-color: var(--color-primary);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  transition: width 0.3s ease-in-out;
  border-right: 1px solid var(--color-border);
  z-index: 100;
}
.sidebar.collapsed {
  width: 60px;
}

/* Shift sidebar down when the View As top bar is showing */
.sidebar.view-as-offset {
  top: 36px;
  height: calc(100vh - 36px);
}

body:has(.view-as-topbar--demo) .sidebar.view-as-offset {
  top: 48px;
  height: calc(100vh - 48px);
}

/* Logo Zone — dedicated strip with its own background (white by default) */
.logo-zone {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  min-height: 72px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.sidebar-logo img {
  max-width: 160px;
  max-height: 52px;
  object-fit: contain;
}

.sidebar-logo--collapsed img.collapsed-logo {
  max-width: 36px;
  max-height: 36px;
  object-fit: contain;
}

.collapsed-initials {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--color-primary, #212938);
}

.text-logo-dark {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--color-primary, #212938);
}

/* Toggle handle — absolute circle at bottom-right of logo zone */
.toggle-handle {
  position: absolute;
  bottom: -14px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--color-surface, #ffffff);
  border: 1.5px solid var(--color-border, #d9e2ef);
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-primary, #212938);
  font-size: 0.65rem;
  transition: background 0.15s, box-shadow 0.15s;
  z-index: 10;
}
.toggle-handle:hover {
  background: #f0f4ff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.16);
}
.logo-link {
  display: flex;
  align-items: center;
  flex-grow: 1;
}
.logo-full {
  height: 28px;
  margin-left: var(--spacing-sm);
}
.logo-small {
  height: 30px;
}
/* Navigation */
.sidebar-nav {
  flex-grow: 1;
  min-height: 0;
  padding-top: var(--spacing-sm);
  overflow-y: auto;
  overflow-x: hidden;
  /* Custom scrollbar styling for better UX */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
}

/* Webkit scrollbar styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}
.sidebar-nav::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar-nav::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}
.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.5);
}
.nav-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  margin: var(--spacing-xs) var(--spacing-sm);
  color: #ffffff;
  text-decoration: none;
  transition: background-color 0.2s;
  white-space: nowrap;
  border-radius: var(--border-radius);
}
.nav-item i:not(.submenu-chevron) {
  margin-right: var(--spacing-md);
  width: 20px;
  text-align: center;
  font-size: 1.1rem;
}

.nav-item-split {
  display: flex;
  align-items: center;
  padding: 0;
  gap: 0;
}

.nav-item-split:has(.router-link-active) {
  background-color: var(--color-accent);
  font-weight: 600;
}

.nav-split-link {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  color: inherit;
  text-decoration: none;
  border-radius: var(--border-radius);
}

.nav-split-link i:not(.submenu-chevron) {
  margin-right: var(--spacing-md);
  width: 20px;
  text-align: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.submenu-chevron-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  align-items: center;
  line-height: 1;
}

.submenu-chevron-btn .submenu-chevron {
  margin-right: 0;
  width: auto;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
.router-link-exact-active {
  background-color: var(--color-accent);
  font-weight: 600;
}

/* Navigation sections */
.nav-section {
  margin-bottom: var(--spacing-lg);
}

.nav-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin: var(--spacing-md) var(--spacing-md) var(--spacing-sm) var(--spacing-md);
  padding: 0;
}

.nav-section-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: inherit;
  text-align: left;
}

.nav-section-toggle:hover .nav-section-title {
  color: rgba(255, 255, 255, 0.95);
}

.nav-section-toggle .nav-section-title {
  margin-bottom: var(--spacing-sm);
}

.nav-section-toggle .section-chevron {
  margin: 0 var(--spacing-md) var(--spacing-sm) 0;
  font-size: 0.65rem;
  opacity: 0.6;
  flex-shrink: 0;
}

/* Administration section spacing */
.administration-section {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-md);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Submenu styling */
.submenu {
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: var(--border-radius);
  margin: var(--spacing-xs) var(--spacing-sm);
  padding: var(--spacing-xs) 0;
}

.sub-item {
  margin-left: var(--spacing-lg);
  font-size: 0.9rem;
  opacity: 0.9;
}

.sub-item-indent {
  margin-left: calc(var(--spacing-lg) + 10px);
  font-size: 0.85rem;
  opacity: 0.8;
}

.submenu-nested {
  margin: 0 var(--spacing-sm) var(--spacing-xs) var(--spacing-sm);
  margin-left: calc(var(--spacing-lg) + var(--spacing-md));
  padding: var(--spacing-xs) 0;
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: var(--border-radius);
}

.nav-sub-compare {
  font-size: 0.78rem;
  padding: 6px 8px 6px 10px;
  white-space: nowrap;
  letter-spacing: -0.01em;
  margin-left: var(--spacing-sm);
}

.nav-sub-compare-label {
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.nav-stand-mirror-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  flex-shrink: 0;
  margin-right: 10px;
  color: inherit;
}

.nav-stand-mirror-icon svg {
  width: 15px;
  height: 15px;
  display: block;
}

.nav-item-toggle {
  cursor: pointer;
  user-select: none;
  width: 100%;
  box-sizing: border-box;
  gap: 0;
}

.submenu-chevron {
  margin-left: auto;
  font-size: 0.65rem;
  opacity: 0.6;
  flex-shrink: 0;
  width: auto !important;
  transition: transform 0.2s;
}

.sub-item:hover {
  opacity: 1;
}

.nav-item-disabled {
  cursor: default;
  opacity: 0.45;
  pointer-events: none;
}

.nav-coming-soon {
  margin-left: auto;
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #008ca6;
  background: #e5f9fd;
  border-radius: 4px;
  padding: 1px 5px;
  flex-shrink: 0;
}

/* Submenu arrow and expandable items */
.has-submenu {
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.submenu-arrow {
  transition: transform 0.3s ease;
  font-size: 0.8rem;
  margin-left: auto;
  margin-right: 0;
}

.has-submenu:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Footer styling */
.sidebar-footer {
  padding: var(--spacing-md);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto; /* Push to bottom */
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.footer-info {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.organization-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.organization-badge {
  display: flex;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius);
  padding: var(--spacing-xs) var(--spacing-sm);
  gap: var(--spacing-xs);
}

.organization-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
}

.subscription-tier {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  background-color: rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  font-weight: bold;
}

/* ── Org switcher ──────────────────────────────────────────────── */
.org-switcher-btn {
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border-radius: 6px;
  padding: 4px 6px;
  width: 100%;
  box-sizing: border-box;
  transition: background 0.15s;
  color: #ffffff;
  font-size: 0.8rem;
}
.org-switcher-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}
.org-switcher-btn.viewing-as-active {
  background: rgba(0, 115, 230, 0.25);
  border: 1px solid rgba(0, 115, 230, 0.4);
}
.switcher-chevron {
  margin-left: auto;
  font-size: 0.65rem;
  opacity: 0.7;
  transition: transform 0.2s;
}
.switcher-chevron.flipped {
  transform: rotate(180deg);
}

.nav-switcher-popover {
  background: #1a2030;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 6px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.switcher-active-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.switcher-active-label { color: rgba(255, 255, 255, 0.6); white-space: nowrap; }
.switcher-active-name { color: #ffffff; flex: 1; font-size: 0.78rem; }
.switcher-exit-btn {
  all: unset;
  cursor: pointer;
  color: #f87171;
  font-size: 0.72rem;
  white-space: nowrap;
}
.switcher-exit-btn:hover { color: #fca5a5; }

.switcher-search-label {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.55);
}
.switcher-search-input {
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  color: #ffffff;
  font-size: 0.8rem;
  padding: 5px 8px;
  outline: none;
}
.switcher-search-input::placeholder { color: rgba(255, 255, 255, 0.35); }
.switcher-search-input:focus { border-color: #0073e6; }

.switcher-results { display: flex; flex-direction: column; gap: 2px; }
.switcher-result-item {
  all: unset;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 5px 6px;
  border-radius: 5px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
  transition: background 0.12s;
}
.switcher-result-item:hover { background: rgba(255, 255, 255, 0.1); }
.switcher-org-active {
  background: rgba(255, 255, 255, 0.12);
  font-weight: 600;
}
.switcher-org-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.switcher-current-pill {
  margin-left: auto;
  font-size: 0.65rem;
  background: rgba(45, 179, 133, 0.3);
  color: #7eefc9;
  padding: 1px 5px;
  border-radius: 4px;
}
.switcher-tier-pill {
  margin-left: auto;
  font-size: 0.65rem;
  background: rgba(255, 255, 255, 0.12);
  padding: 1px 5px;
  border-radius: 4px;
  color: rgba(255, 255, 255, 0.6);
}
.switcher-empty {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 4px;
}

.switcher-role-picker { display: flex; flex-direction: column; gap: 5px; }
.switcher-selected-org {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.07);
  padding: 4px 6px;
  border-radius: 5px;
}
.switcher-selected-org strong { flex: 1; }
.switcher-clear-btn {
  all: unset;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.7rem;
}
.switcher-clear-btn:hover { color: rgba(255, 255, 255, 0.9); }
.switcher-role-label { font-size: 0.72rem; color: rgba(255, 255, 255, 0.55); }
.switcher-role-select {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  color: #ffffff;
  font-size: 0.8rem;
  padding: 4px 6px;
  outline: none;
}
.switcher-role-select option { background: #212938; color: #ffffff; }
.switcher-active-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.switcher-active-row { border-bottom: none; padding-bottom: 0; }
.switcher-role-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.switcher-role-row .switcher-role-select { flex: 1; }
.switcher-activate-btn {
  all: unset;
  cursor: pointer;
  background: #0059b2;
  color: #ffffff;
  border-radius: 5px;
  font-size: 0.8rem;
  padding: 6px 10px;
  text-align: center;
  transition: background 0.15s;
}
.switcher-activate-btn:hover { background: #0073e6; }

.user-info {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.user-avatar {
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  flex-shrink: 0;
}

.user-avatar--link {
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.user-avatar--link:hover,
.user-avatar--link:focus-visible {
  background-color: rgba(255, 255, 255, 0.35);
  color: #ffffff;
  outline: none;
}

.user-details {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
}

.user-role {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
}

/* Verified Member Badge */
.member-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-top: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.member-badge i {
  font-size: 0.65rem;
  margin-right: 0;
  width: auto;
}

.logout-btn {
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
}

.footer-bottom-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.footer-bottom-row .logout-btn {
  flex: 1;
  min-width: 0;
}

.logout-btn:hover {
  background-color: rgba(220, 38, 38, 0.2);
  color: #fecaca;
}

/* Demo link special styling */
.nav-item.demo-link {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  margin: 0.5rem 0;
  position: relative;
  overflow: hidden;
}

.nav-item.demo-link:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.nav-item.demo-link.router-link-exact-active {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

.demo-badge {
  background: #e74c3c;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: auto;
  font-weight: bold;
  animation: pulse 2s infinite;
}

.ai-badge {
  background: #27ae60;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: auto;
  font-weight: bold;
}

.internal-badge {
  background: #7c3aed;
  color: white;
}

.admin-badge {
  background: #9b59b6;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: auto;
  font-weight: bold;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.proprietary-badge {
  background: #3498db;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 10px;
  margin-left: auto;
  font-weight: bold;
  animation: pulse 2s infinite;
}

.nav-badge {
  background: #ef4444;
  color: white;
  font-size: 0.65rem;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  margin-left: auto;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
}

/* ── Product / status badges ─────────────────────────────────── */
.tool-badge {
  background: #008ca6;
  color: white;
  font-size: 0.62rem;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  margin-left: auto;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.soon-badge {
  background: #2db385;
  color: white;
  font-size: 0.62rem;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  margin-left: auto;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.legacy-badge {
  background: #b0bac9;
  color: white;
  font-size: 0.62rem;
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  margin-left: auto;
  font-weight: 700;
  letter-spacing: 0.04em;
}

/* ── IWI pinned link ─────────────────────────────────────────── */
.iwi-pin {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 600;
}

.iwi-pin:hover {
  background-color: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

/* ── Standup pinned link ─────────────────────────────────────── */
.standup-pin {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.standup-pin:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

/* ── Demo exit password modal ────────────────────────────────── */
.org-switch-overlay {
  position: fixed;
  inset: 0;
  background: #212938;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.org-switch-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  text-align: center;
  padding: 48px 56px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
}
.org-switch-spinner {
  font-size: 2.8rem;
  color: #2db385;
}
.org-switch-label {
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.org-switch-sub {
  color: #b0bac9;
  font-size: 0.95rem;
  font-weight: 400;
}

.sidebar-demo-exit-overlay {
  position: fixed;
  inset: 0;
  background: rgba(33, 41, 56, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-demo-exit-modal {
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 32px 24px;
  width: 360px;
  max-width: calc(100vw - 32px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.sidebar-demo-exit-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #212938;
}

.sidebar-demo-exit-header .fas {
  color: #008ca6;
}

.sidebar-demo-exit-body {
  font-size: 0.875rem;
  color: #6b7a99;
  margin: 0;
  line-height: 1.5;
}

.sidebar-demo-exit-input {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d9e2ef;
  border-radius: 7px;
  font-size: 0.9rem;
  color: #212938;
  outline: none;
  transition: border-color 0.15s;
}

.sidebar-demo-exit-input:focus {
  border-color: #0059b2;
}

.sidebar-demo-exit-error {
  font-size: 0.8rem;
  color: #c0392b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

.sidebar-demo-exit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}

.sidebar-demo-exit-cancel {
  padding: 7px 16px;
  border: 1px solid #d9e2ef;
  border-radius: 7px;
  background: #ffffff;
  color: #6b7a99;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.sidebar-demo-exit-cancel:hover {
  background: #f5f8ff;
}

.sidebar-demo-exit-confirm {
  padding: 7px 18px;
  border: none;
  border-radius: 7px;
  background: #0059b2;
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.sidebar-demo-exit-confirm:hover:not(:disabled) {
  background: #0073e6;
}

.sidebar-demo-exit-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

</style> 